import { useCallback, useReducer, useRef } from 'react'
import { useAuthContext } from '@/auth/AuthProvider'
import { apiFetch } from '@/api/client'

/**
 * Mutation hook for `DELETE /api/initiatives/{id}`. Different shape from
 * `useApiMutation` because the path varies per id — the id is supplied
 * at mutate-time, not hook-construction-time.
 *
 * On success, the API returns 204 (no body) and `apiFetch` resolves to
 * `undefined`. The hook's state transitions to `success`. Caller
 * navigates away (back to the list) on success since the row no longer
 * exists in the default-active set.
 */
export type DeleteState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success' }
  | { status: 'error'; error: Error }

export interface UseDeleteInitiativeResult {
  state: DeleteState
  mutate: (initiativeId: number) => Promise<boolean>
  reset: () => void
}

type DeleteAction =
  | { type: 'FETCH' }
  | { type: 'SUCCESS' }
  | { type: 'ERROR'; error: Error }
  | { type: 'RESET' }

function reducer(state: DeleteState, action: DeleteAction): DeleteState {
  switch (action.type) {
    case 'FETCH':
      return { status: 'loading' }
    case 'SUCCESS':
      return { status: 'success' }
    case 'ERROR':
      return { status: 'error', error: action.error }
    case 'RESET':
      return { status: 'idle' }
    default:
      return state
  }
}

export function useDeleteInitiative(): UseDeleteInitiativeResult {
  const { getToken } = useAuthContext()
  const [state, dispatch] = useReducer(reducer, { status: 'idle' })

  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const seqRef = useRef(0)

  const mutate = useCallback(async (initiativeId: number): Promise<boolean> => {
    const seq = ++seqRef.current
    dispatch({ type: 'FETCH' })
    try {
      await apiFetch<void>(`/api/initiatives/${initiativeId}`, () => getTokenRef.current(), {
        method: 'DELETE',
      })
      if (seq !== seqRef.current) return false
      dispatch({ type: 'SUCCESS' })
      return true
    } catch (err) {
      if (seq !== seqRef.current) return false
      dispatch({
        type: 'ERROR',
        error: err instanceof Error ? err : new Error(String(err)),
      })
      return false
    }
  }, [])

  const reset = useCallback(() => dispatch({ type: 'RESET' }), [])

  return { state, mutate, reset }
}
